import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;
const API_URL = process.env.REACT_APP_API_URL || '/api';

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; background: #1a1a2e; min-height: 100vh; color: white; }
    video { border-radius: 12px; background: #0f0f1a; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  `,
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  logo: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'white',
  },
  timer: {
    background: 'rgba(255,255,255,0.1)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  liveIndicator: {
    width: '10px',
    height: '10px',
    background: '#e74c3c',
    borderRadius: '50%',
    animation: 'pulse 1s infinite',
  },
  videoContainer: {
    flex: 1,
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
  },
  videoWrapper: {
    flex: 1,
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#0f0f1a',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '16px',
  },
  videoLabel: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    background: 'rgba(0,0,0,0.6)',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
  },
  localVideoWrapper: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '200px',
    height: '150px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '3px solid rgba(255,255,255,0.2)',
    background: '#0f0f1a',
  },
  localVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)',
  },
  noVideo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
  },
  noVideoIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '20px',
  },
  controlButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  muteButton: {
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
  },
  muteButtonActive: {
    background: '#e74c3c',
    color: 'white',
  },
  videoButton: {
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
  },
  videoButtonActive: {
    background: '#e74c3c',
    color: 'white',
  },
  endButton: {
    background: '#e74c3c',
    color: 'white',
    width: '80px',
  },
  statusBar: {
    textAlign: 'center',
    padding: '12px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  statusText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
  },
  matchInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  matchAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
  },
  matchName: {
    fontSize: '18px',
    fontWeight: '600',
  },
  matchRole: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
  },
  errorBox: {
    background: 'rgba(231, 76, 60, 0.2)',
    border: '1px solid rgba(231, 76, 60, 0.3)',
    borderRadius: '12px',
    padding: '16px',
    margin: '20px 0',
    textAlign: 'center',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '14px',
  },
  retryButton: {
    marginTop: '12px',
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
};

// ICE servers with TURN for better connectivity
// Using free public TURN servers - for production, use your own or a paid service
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    // Free TURN servers from Open Relay Project
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443?transport=tcp',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ],
  iceCandidatePoolSize: 10
};

function InterviewPage({ user, setUser }) {
  const navigate = useNavigate();
  
  // All mutable state in refs to avoid stale closures
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Store interview data in refs so handlers always have current values
  const interviewDataRef = useRef(null);
  const isInitiatorRef = useRef(false);
  const roomIdRef = useRef(null);
  const peerIdRef = useRef(null);
  
  // Guards
  const isInitialized = useRef(false);
  const hasCreatedOffer = useRef(false);
  const hasReceivedOffer = useRef(false);
  const hasReceivedAnswer = useRef(false);
  const iceCandidatesQueue = useRef([]);
  const peerJoined = useRef(false);
  const connectionCheckIntervalRef = useRef(null);
  const callStartTimeRef = useRef(Date.now());

  // React state for UI rendering only
  const [interviewData, setInterviewData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [mediaError, setMediaError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [hasLocalVideo, setHasLocalVideo] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('Cleaning up WebRTC resources...');
    console.log('- connectionCheckIntervalRef:', !!connectionCheckIntervalRef.current);
    console.log('- localStreamRef:', !!localStreamRef.current);
    console.log('- peerConnectionRef:', !!peerConnectionRef.current);
    console.log('- socketRef:', !!socketRef.current);
    
    if (connectionCheckIntervalRef.current) {
      clearInterval(connectionCheckIntervalRef.current);
      connectionCheckIntervalRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind);
        track.stop();
      });
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      console.log('Closing peer connection, state:', peerConnectionRef.current.connectionState);
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (socketRef.current) {
      console.log('Disconnecting socket');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    // Reset guards
    hasCreatedOffer.current = false;
    hasReceivedOffer.current = false;
    hasReceivedAnswer.current = false;
    iceCandidatesQueue.current = [];
    peerJoined.current = false;
    
    console.log('Cleanup complete');
  }, []);

  // Initialize on mount
  useEffect(() => {
    // Check both ref and sessionStorage to prevent double init
    const data = sessionStorage.getItem('interviewData');
    if (!data) {
      console.log('No interview data, redirecting to home');
      navigate('/home');
      return;
    }

    // Prevent double initialization (React StrictMode protection)
    if (isInitialized.current) {
      console.log('Already initialized, skipping...');
      return;
    }
    
    // Mark as initializing BEFORE parsing to prevent race conditions
    isInitialized.current = true;
    callStartTimeRef.current = Date.now();

    const parsed = JSON.parse(data);
    
    // Store in refs for use in handlers (avoids stale closures)
    interviewDataRef.current = parsed;
    isInitiatorRef.current = parsed.isInitiator;
    roomIdRef.current = parsed.roomId;
    peerIdRef.current = parsed.peerId;
    
    // Store in state for UI rendering
    setInterviewData(parsed);
    
    console.log('=== INTERVIEW INIT ===');
    console.log('isInitiator:', parsed.isInitiator);
    console.log('roomId:', parsed.roomId);
    console.log('peerId:', parsed.peerId);
    console.log('timestamp:', new Date().toISOString());
    
    initializeCall();

    // Duration timer
    const durationInterval = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => {
      console.log('Cleanup triggered, isInitialized:', isInitialized.current);
      clearInterval(durationInterval);
      
      // Only cleanup if we were actually initialized
      // Don't cleanup on StrictMode's first unmount
      if (isInitialized.current) {
        cleanup();
      }
      
      // Note: We intentionally don't reset isInitialized here
      // to prevent StrictMode from re-initializing
    };
  }, [navigate, cleanup]);

  // Ensure local video element has the stream
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current && hasLocalVideo && !isVideoOff) {
      if (!localVideoRef.current.srcObject) {
        console.log('useEffect: Applying local stream to video element');
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    }
  }, [hasLocalVideo, isVideoOff]);

  const getMediaStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasLocalVideo(true);
      return stream;
    } catch (err) {
      console.log('Failed to get video+audio:', err.message);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasLocalVideo(false);
      setMediaError('Camera not available. Using audio only.');
      return stream;
    } catch (err) {
      console.log('Failed to get audio:', err.message);
    }

    setMediaError('Could not access camera or microphone. Please check permissions.');
    return null;
  };

  const initializeCall = async () => {
    try {
      setConnectionStatus('connecting');
      
      // Get local media FIRST
      const stream = await getMediaStream();
      if (stream) {
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      }

      // Create peer connection
      createPeerConnection();

      // Connect socket
      const token = localStorage.getItem('token');
      socketRef.current = io(SOCKET_URL, { 
        auth: { token },
        transports: ['websocket', 'polling']
      });

      socketRef.current.on('connect', () => {
        const roomId = roomIdRef.current;
        const isInitiator = isInitiatorRef.current;
        
        console.log('Socket connected, joining room:', roomId, 'isInitiator:', isInitiator);
        socketRef.current.emit('join-room', { roomId });
        
        // Fallback: If initiator and no offer created after 2 seconds, create one anyway
        // This handles race conditions where both users join simultaneously
        if (isInitiator) {
          setTimeout(() => {
            if (!hasCreatedOffer.current) {
              console.log('Fallback: Creating offer after timeout (no user-joined/session-ready received)');
              createOffer();
            }
          }, 2000);
        }
      });

      // Wait for peer to join before creating offer
      socketRef.current.on('user-joined', ({ userId, isReconnect }) => {
        console.log('User joined room:', userId, 'isReconnect:', isReconnect);
        peerJoined.current = true;
        
        // If I'm the initiator and haven't created an offer yet, create one now
        if (isInitiatorRef.current && !hasCreatedOffer.current) {
          console.log('Peer joined, I am initiator - creating offer now');
          setTimeout(() => createOffer(), 500);
        }
      });

      // Session is ready with both users
      socketRef.current.on('session-ready', ({ roomId, shouldRenegotiate }) => {
        console.log('Session ready:', roomId, 'shouldRenegotiate:', shouldRenegotiate);
        peerJoined.current = true;
        
        // If initiator and haven't created offer, do it now
        if (isInitiatorRef.current && !hasCreatedOffer.current) {
          console.log('Session ready, creating offer as initiator');
          setTimeout(() => createOffer(), 500);
        }
      });

      socketRef.current.on('webrtc-offer', async ({ userId, offer }) => {
        if (isInitiatorRef.current) {
          console.log('I am initiator, ignoring offer');
          return;
        }
        
        if (hasReceivedOffer.current) {
          console.log('Already received offer, ignoring');
          return;
        }
        
        hasReceivedOffer.current = true;
        console.log('Processing offer from:', userId);
        await handleOffer(offer);
      });

      socketRef.current.on('webrtc-answer', async ({ userId, answer }) => {
        if (!isInitiatorRef.current) {
          console.log('Not initiator, ignoring answer');
          return;
        }
        
        if (hasReceivedAnswer.current) {
          console.log('Already received answer, ignoring');
          return;
        }
        
        hasReceivedAnswer.current = true;
        console.log('Processing answer from:', userId);
        await handleAnswer(answer);
      });

      socketRef.current.on('webrtc-ice-candidate', async ({ userId, candidate }) => {
        await handleIceCandidate(candidate);
      });

      socketRef.current.on('interview-ended', () => {
        // Debounce: ignore if call just started (race condition protection)
        const callAge = Date.now() - callStartTimeRef.current;
        const pc = peerConnectionRef.current;
        
        console.log('Received interview-ended event, call age:', callAge, 'ms, connection state:', pc?.connectionState);
        
        // Ignore if call started less than 5 seconds ago - likely a race condition
        if (callAge < 5000) {
          console.log('Ignoring interview-ended - call just started, likely race condition');
          return;
        }
        
        console.log('Interview ended by other party');
        cleanup();
        sessionStorage.removeItem('interviewData');
        navigate('/home');
      });

      // Handle peer disconnection
      socketRef.current.on('peer-disconnected', ({ userId, message }) => {
        console.log('Peer disconnected:', userId, message);
        setConnectionStatus('reconnecting');
      });

      // Handle renegotiation for reconnection
      socketRef.current.on('start-renegotiation', async ({ isInitiator, peerId }) => {
        console.log('Starting renegotiation, isInitiator:', isInitiator);
        hasCreatedOffer.current = false;
        hasReceivedOffer.current = false;
        hasReceivedAnswer.current = false;
        
        if (isInitiator) {
          setTimeout(() => createOffer(), 500);
        }
      });

      // Periodic check: retry offer if initiator and not connected after 5 seconds
      connectionCheckIntervalRef.current = setInterval(() => {
        const pc = peerConnectionRef.current;
        const isConnected = pc?.connectionState === 'connected' || pc?.iceConnectionState === 'connected';
        
        if (!isConnected && isInitiatorRef.current) {
          console.log('Connection check: Not connected yet, retrying offer...');
          hasCreatedOffer.current = false;
          createOffer();
        } else if (isConnected) {
          // Connected, stop checking
          clearInterval(connectionCheckIntervalRef.current);
          connectionCheckIntervalRef.current = null;
        }
      }, 5000);

    } catch (error) {
      console.error('Error initializing call:', error);
      setConnectionStatus('error');
      setMediaError(error.message);
    }
  };

  const createPeerConnection = () => {
    if (peerConnectionRef.current) {
      console.log('Peer connection already exists');
      return;
    }
    
    console.log('Creating peer connection with ICE servers:', ICE_SERVERS.iceServers.length);
    peerConnectionRef.current = new RTCPeerConnection(ICE_SERVERS);

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Adding local track:', track.kind, track.enabled);
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });
    } else {
      console.warn('No local stream available when creating peer connection');
    }

    // Handle incoming tracks
    peerConnectionRef.current.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind, 'streams:', event.streams.length);
      
      if (event.streams && event.streams[0]) {
        remoteStreamRef.current = event.streams[0];
        setHasRemoteVideo(true);
        
        if (remoteVideoRef.current) {
          console.log('Setting remote video srcObject');
          remoteVideoRef.current.srcObject = event.streams[0];
          remoteVideoRef.current.play().catch(err => {
            console.log('Remote video play error:', err);
          });
        }
      }
      setConnectionStatus('connected');
    };

    // Handle ICE candidates
    peerConnectionRef.current.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        console.log('Sending ICE candidate:', event.candidate.type);
        socketRef.current.emit('webrtc-ice-candidate', {
          roomId: roomIdRef.current,
          candidate: event.candidate
        });
      }
    };

    // Handle ICE gathering state
    peerConnectionRef.current.onicegatheringstatechange = () => {
      console.log('ICE gathering state:', peerConnectionRef.current?.iceGatheringState);
    };

    // Handle connection state changes
    peerConnectionRef.current.onconnectionstatechange = () => {
      const state = peerConnectionRef.current?.connectionState;
      console.log('Connection state:', state);
      
      switch (state) {
        case 'connected':
          setConnectionStatus('connected');
          break;
        case 'disconnected':
          setConnectionStatus('reconnecting');
          break;
        case 'failed':
          setConnectionStatus('error');
          // Try to restart ICE
          restartIce();
          break;
        case 'closed':
          setConnectionStatus('disconnected');
          break;
        default:
          break;
      }
    };

    // Handle ICE connection state
    peerConnectionRef.current.oniceconnectionstatechange = () => {
      const state = peerConnectionRef.current?.iceConnectionState;
      console.log('ICE connection state:', state);
      
      if (state === 'failed') {
        console.log('ICE connection failed, attempting restart...');
        restartIce();
      }
    };

    // Handle negotiation needed (for adding tracks later)
    peerConnectionRef.current.onnegotiationneeded = () => {
      console.log('Negotiation needed');
      // Only initiator should create offers on negotiation needed
      if (isInitiatorRef.current && peerJoined.current && !hasCreatedOffer.current) {
        createOffer();
      }
    };
  };

  const restartIce = async () => {
    if (!peerConnectionRef.current || !isInitiatorRef.current) return;
    
    console.log('Restarting ICE...');
    try {
      const offer = await peerConnectionRef.current.createOffer({ iceRestart: true });
      await peerConnectionRef.current.setLocalDescription(offer);
      
      socketRef.current?.emit('webrtc-offer', { 
        roomId: roomIdRef.current, 
        offer 
      });
    } catch (error) {
      console.error('Error restarting ICE:', error);
    }
  };

  const createOffer = async () => {
    if (hasCreatedOffer.current) {
      console.log('Offer already created');
      return;
    }
    
    if (!peerConnectionRef.current) {
      console.log('No peer connection, cannot create offer');
      return;
    }
    
    hasCreatedOffer.current = true;
    
    try {
      console.log('Creating offer...');
      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      console.log('Setting local description...');
      await peerConnectionRef.current.setLocalDescription(offer);
      
      console.log('Sending offer to room:', roomIdRef.current);
      socketRef.current?.emit('webrtc-offer', { 
        roomId: roomIdRef.current, 
        offer 
      });
      console.log('Offer sent');
    } catch (error) {
      console.error('Error creating offer:', error);
      hasCreatedOffer.current = false;
    }
  };

  const handleOffer = async (offer) => {
    if (!peerConnectionRef.current) {
      console.log('No peer connection, cannot handle offer');
      return;
    }
    
    try {
      console.log('Setting remote description from offer...');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Process queued ICE candidates
      console.log('Processing', iceCandidatesQueue.current.length, 'queued ICE candidates');
      while (iceCandidatesQueue.current.length > 0) {
        const candidate = iceCandidatesQueue.current.shift();
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('Error adding queued ICE candidate:', e);
        }
      }
      
      console.log('Creating answer...');
      const answer = await peerConnectionRef.current.createAnswer();
      
      console.log('Setting local description (answer)...');
      await peerConnectionRef.current.setLocalDescription(answer);
      
      console.log('Sending answer to room:', roomIdRef.current);
      socketRef.current?.emit('webrtc-answer', { 
        roomId: roomIdRef.current, 
        answer 
      });
      console.log('Answer sent');
    } catch (error) {
      console.error('Error handling offer:', error);
      hasReceivedOffer.current = false;
    }
  };

  const handleAnswer = async (answer) => {
    if (!peerConnectionRef.current) {
      console.log('No peer connection, cannot handle answer');
      return;
    }
    
    try {
      console.log('Setting remote description from answer...');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      
      // Process queued ICE candidates
      console.log('Processing', iceCandidatesQueue.current.length, 'queued ICE candidates');
      while (iceCandidatesQueue.current.length > 0) {
        const candidate = iceCandidatesQueue.current.shift();
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('Error adding queued ICE candidate:', e);
        }
      }
      console.log('Remote description set successfully');
    } catch (error) {
      console.error('Error handling answer:', error);
      hasReceivedAnswer.current = false;
    }
  };

  const handleIceCandidate = async (candidate) => {
    if (!candidate) return;
    
    try {
      if (peerConnectionRef.current?.remoteDescription) {
        console.log('Adding ICE candidate:', candidate.type || 'unknown');
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        console.log('Queueing ICE candidate (no remote description yet)');
        iceCandidatesQueue.current.push(candidate);
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  const toggleMute = () => {
    if (!localStreamRef.current) {
      console.log('No local stream for mute toggle');
      return;
    }
    
    const audioTracks = localStreamRef.current.getAudioTracks();
    console.log('Audio tracks:', audioTracks.length);
    
    if (audioTracks.length === 0) {
      console.log('No audio tracks available');
      return;
    }
    
    const newMutedState = !isMuted;
    audioTracks.forEach(track => {
      track.enabled = !newMutedState; // enabled = true when NOT muted
      console.log('Audio track enabled:', track.enabled);
    });
    
    setIsMuted(newMutedState);
    console.log('Muted state set to:', newMutedState);
  };

  const toggleVideo = () => {
    if (!localStreamRef.current) {
      console.log('No local stream for video toggle');
      return;
    }
    
    const videoTracks = localStreamRef.current.getVideoTracks();
    console.log('Video tracks:', videoTracks.length);
    
    if (videoTracks.length === 0) {
      console.log('No video tracks available');
      return;
    }
    
    const newVideoOffState = !isVideoOff;
    videoTracks.forEach(track => {
      track.enabled = !newVideoOffState; // enabled = true when video is ON
      console.log('Video track enabled:', track.enabled);
    });
    
    setIsVideoOff(newVideoOffState);
    console.log('Video off state set to:', newVideoOffState);
  };

  const endCall = async () => {
    // Prevent accidental calls in the first 5 seconds
    const callAge = Date.now() - callStartTimeRef.current;
    if (callAge < 5000) {
      console.log('Ignoring endCall - call just started, call age:', callAge, 'ms');
      return;
    }
    
    console.log('End call initiated by user, call age:', callAge, 'ms');
    
    if (peerIdRef.current) {
      socketRef.current?.emit('end-interview', { peerId: peerIdRef.current });
    }
    cleanup();
    sessionStorage.removeItem('interviewData');
    
    try {
      const response = await fetch(`${API_URL}/auth/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isInterviewing: false, lookingForInterview: false })
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data.user);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
    
    navigate('/home');
  };

  const retryMedia = async () => {
    setMediaError(null);
    const stream = await getMediaStream();
    if (stream) {
      // Stop old tracks
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Replace tracks in peer connection
      if (peerConnectionRef.current) {
        const senders = peerConnectionRef.current.getSenders();
        stream.getTracks().forEach(track => {
          const sender = senders.find(s => s.track?.kind === track.kind);
          if (sender) {
            sender.replaceTrack(track);
          } else {
            peerConnectionRef.current.addTrack(track, stream);
          }
        });
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!interviewData) {
    return null;
  }

  return (
    <>
      <style>{styles.global}</style>

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logo}>Instavju</div>
          <div style={styles.timer}>
            <div style={styles.liveIndicator}></div>
            {formatDuration(callDuration)}
          </div>
        </div>

        {interviewData.matchedUser && (
          <div style={styles.matchInfo}>
            <div style={styles.matchAvatar}>
              {interviewData.matchedUser.firstName?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={styles.matchName}>
                {interviewData.matchedUser.firstName} {interviewData.matchedUser.lastName}
              </div>
              <div style={styles.matchRole}>
                {interviewData.matchedUser.role === 'hiring_manager' ? 'Hiring Manager' : 'Job Seeker'}
              </div>
            </div>
          </div>
        )}

        {mediaError && (
          <div style={styles.errorBox}>
            <div style={styles.errorText}>⚠️ {mediaError}</div>
            <button onClick={retryMedia} style={styles.retryButton}>
              Retry Camera/Mic
            </button>
          </div>
        )}

        <div style={styles.statusBar}>
          <span style={styles.statusText}>
            {connectionStatus === 'connecting' && '🟡 Connecting...'}
            {connectionStatus === 'connected' && '🟢 Connected'}
            {connectionStatus === 'reconnecting' && '🟡 Reconnecting...'}
            {connectionStatus === 'disconnected' && '🔴 Disconnected'}
            {connectionStatus === 'error' && '🔴 Connection error - retrying...'}
          </span>
        </div>

        <div style={styles.videoContainer}>
          <div style={styles.videoWrapper}>
            {hasRemoteVideo ? (
              <video
                ref={(el) => {
                  remoteVideoRef.current = el;
                  if (el && remoteStreamRef.current && !el.srcObject) {
                    console.log('Applying stored remote stream to video element');
                    el.srcObject = remoteStreamRef.current;
                    el.play().catch(err => console.log('Play error:', err));
                  }
                }}
                autoPlay
                playsInline
                style={styles.video}
              />
            ) : (
              <div style={styles.noVideo}>
                <div style={styles.noVideoIcon}>👤</div>
                <div>Waiting for {interviewData.matchedUser?.firstName}'s video...</div>
              </div>
            )}
            <div style={styles.videoLabel}>
              {interviewData.matchedUser?.firstName || 'Participant'}
            </div>
            
            <div style={styles.localVideoWrapper}>
              {hasLocalVideo && !isVideoOff ? (
                <video
                  ref={(el) => {
                    localVideoRef.current = el;
                    // Apply stream when element mounts
                    if (el && localStreamRef.current && !el.srcObject) {
                      console.log('Applying local stream to video element');
                      el.srcObject = localStreamRef.current;
                    }
                  }}
                  autoPlay
                  playsInline
                  muted
                  style={styles.localVideo}
                />
              ) : (
                <div style={{ ...styles.noVideo, height: '100%' }}>
                  <div style={{ fontSize: '24px' }}>📵</div>
                  <div style={{ fontSize: '11px' }}>Camera off</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.controls}>
          <button
            onClick={toggleMute}
            style={{
              ...styles.controlButton,
              ...(isMuted ? styles.muteButtonActive : styles.muteButton)
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🎤'}
          </button>

          <button
            onClick={toggleVideo}
            style={{
              ...styles.controlButton,
              ...(isVideoOff ? styles.videoButtonActive : styles.videoButton)
            }}
            title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            disabled={!hasLocalVideo}
          >
            {isVideoOff ? '📵' : '📹'}
          </button>

          <button
            onClick={endCall}
            style={{ 
              ...styles.controlButton, 
              ...styles.endButton,
              opacity: callDuration < 5 ? 0.5 : 1,
              cursor: callDuration < 5 ? 'not-allowed' : 'pointer'
            }}
            title={callDuration < 5 ? 'Please wait...' : 'End call'}
            disabled={callDuration < 5}
          >
            📞
          </button>
        </div>
      </div>
    </>
  );
}

export default InterviewPage;